<script setup lang="ts">
const { t, locale, locales } = useI18n()
const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()
const runtimeConfig = useRuntimeConfig()

const currentYear = new Date().getFullYear()

const businessDisclosureItems = computed(() => [
  { label: t('footer.business.registeredAddress'), value: runtimeConfig.public.businessRegisteredAddress as string },
  { label: t('footer.business.email'), value: runtimeConfig.public.businessEmail as string },
  { label: t('footer.business.phone'), value: runtimeConfig.public.businessPhone as string },
].filter(item => item.value))

// Category codes must match the backend (`/api/app/v1/trade-in/categories`):
// iphone, ipad, macbook, watch. The shop page reads `?category=<code>`.
const shopLinks = [
  { labelKey: 'footer.shop.iphones', category: 'iphone' },
  { labelKey: 'footer.shop.ipads', category: 'ipad' },
  { labelKey: 'footer.shop.macs', category: 'macbook' },
  { labelKey: 'footer.shop.watch', category: 'watch' }
]

const sellLinks = [
  { labelKey: 'footer.sell.getQuote', to: '/compramos' },
  { labelKey: 'footer.sell.tradeIn', to: '/compramos#trade-in' },
  { labelKey: 'footer.sell.process', to: '/compramos#process' },
  { labelKey: 'footer.sell.faqs', to: '/compramos#faq' }
]

const servicesLinks = [
  { labelKey: 'footer.services.repairs', to: '/restauracion' },
  { labelKey: 'footer.services.trackOrder', to: '/cuenta/orders' }
]

const companyLinks = [
  { labelKey: 'footer.company.about', to: '/p/about-us' },
  { labelKey: 'footer.company.blog', to: '/blog' }
]

const legalLinks = [
  { labelKey: 'footer.legal.terms', to: '/p/terms-of-service' },
  { labelKey: 'footer.legal.privacy', to: '/p/privacy-policy' },
  { labelKey: 'footer.legal.cookies', to: '/p/cookie-policy' },
  { labelKey: 'footer.legal.returns', to: '/p/return-and-warranty-policy' },
]

const socialLinks = [
  { name: 'Instagram', icon: 'i-lucide-instagram', url: 'https://instagram.com/midigi.net' },
  { name: 'Facebook', icon: 'i-lucide-facebook', url: 'https://facebook.com/midigi.net' },
  { name: 'Twitter', icon: 'i-lucide-twitter', url: 'https://twitter.com/midigi_net' },
  { name: 'YouTube', icon: 'i-lucide-youtube', url: 'https://youtube.com/@midigi.net' }
]

const paymentMethods = [
  { name: 'Visa', icon: 'i-lucide-credit-card' },
  { name: 'Mastercard', icon: 'i-lucide-credit-card' },
  { name: 'PayPal', icon: 'i-lucide-wallet' },
  { name: 'Apple Pay', icon: 'i-lucide-smartphone' },
  { name: 'Klarna', icon: 'i-lucide-credit-card' }
]

const newsletterEmail = ref('')
const isSubscribed = ref(false)

const subscribeNewsletter = () => {
  if (newsletterEmail.value) {
    isSubscribed.value = true
    setTimeout(() => {
      isSubscribed.value = false
      newsletterEmail.value = ''
    }, 3000)
  }
}
</script>

<template>
  <footer class="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-t border-gray-200 dark:border-gray-800">
    <!-- Main Footer Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <!-- Link Columns Grid -->
      <div class="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12">
        <!-- Shop Column -->
        <div>
          <h3 class="text-gray-900 dark:text-white font-semibold text-xs uppercase tracking-wide mb-3">
            {{ t('footer.columns.shop') }}
          </h3>
          <ul class="space-y-1.5 sm:space-y-2">
            <li v-for="link in shopLinks" :key="link.category">
              <NuxtLink
                :to="{ path: localePath('/shop'), query: { category: link.category } }"
                class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {{ t(link.labelKey) }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <!-- Sell Column -->
        <div>
          <h3 class="text-gray-900 dark:text-white font-semibold text-xs uppercase tracking-wide mb-3">
            {{ t('footer.columns.sell') }}
          </h3>
          <ul class="space-y-1.5 sm:space-y-2">
            <li v-for="link in sellLinks" :key="link.to">
              <NuxtLink
                :to="localePath(link.to)"
                class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {{ t(link.labelKey) }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <!-- Services Column -->
        <div>
          <h3 class="text-gray-900 dark:text-white font-semibold text-xs uppercase tracking-wide mb-3">
            {{ t('footer.columns.services') }}
          </h3>
          <ul class="space-y-1.5 sm:space-y-2">
            <li v-for="link in servicesLinks" :key="link.to">
              <NuxtLink
                :to="localePath(link.to)"
                class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {{ t(link.labelKey) }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <!-- Company Column -->
        <div>
          <h3 class="text-gray-900 dark:text-white font-semibold text-xs uppercase tracking-wide mb-3">
            {{ t('footer.columns.company') }}
          </h3>
          <ul class="space-y-1.5 sm:space-y-2">
            <li v-for="link in companyLinks" :key="link.to">
              <NuxtLink
                :to="localePath(link.to)"
                class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {{ t(link.labelKey) }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <!-- Legal Column -->
        <div>
          <h3 class="text-gray-900 dark:text-white font-semibold text-xs uppercase tracking-wide mb-3">
            {{ t('footer.columns.legal') }}
          </h3>
          <ul class="space-y-1.5 sm:space-y-2">
            <li v-for="link in legalLinks" :key="link.to">
              <NuxtLink
                :to="localePath(link.to)"
                class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {{ t(link.labelKey) }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <!-- Newsletter Column -->
        <div>
          <h3 class="text-gray-900 dark:text-white font-semibold text-xs uppercase tracking-wide mb-3">
            {{ t('footer.columns.connect') }}
          </h3>
          <!-- Social Links (Mobile: just icons, no newsletter form) -->
          <div class="flex items-center gap-2 lg:hidden">
            <a
              v-for="social in socialLinks"
              :key="social.name"
              :href="social.url"
              target="_blank"
              rel="noopener noreferrer"
              class="w-8 h-8 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg flex items-center justify-center transition-colors"
              :aria-label="social.name"
            >
              <UIcon :name="social.icon" class="w-4 h-4" />
            </a>
          </div>
          <!-- Desktop: Full newsletter form -->
          <div class="hidden lg:block">
            <p class="text-sm mb-3 text-gray-500 dark:text-gray-400">
              {{ t('footer.newsletter.text') }}
            </p>
            <form @submit.prevent="subscribeNewsletter" class="mb-4">
              <div class="flex gap-2">
                <input
                  v-model="newsletterEmail"
                  type="email"
                  required
                  :placeholder="t('footer.newsletter.placeholder')"
                  class="flex-1 min-w-0 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                <button
                  type="submit"
                  :aria-label="t('a11y.subscribe')"
                  class="flex-shrink-0 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  <UIcon name="i-lucide-arrow-right" class="w-4 h-4" />
                </button>
              </div>
              <Transition name="fade">
                <p v-if="isSubscribed" class="text-green-600 dark:text-green-400 text-xs mt-2">
                  {{ t('footer.newsletter.success') }}
                </p>
              </Transition>
            </form>
            <div class="flex items-center gap-3">
              <a
                v-for="social in socialLinks"
                :key="social.name"
                :href="social.url"
                target="_blank"
                rel="noopener noreferrer"
                class="w-9 h-9 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg flex items-center justify-center transition-colors"
                :aria-label="social.name"
              >
                <UIcon :name="social.icon" class="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Newsletter Section -->
      <div class="lg:hidden border-t border-gray-200 dark:border-gray-800 pt-6 mb-6">
        <h3 class="text-gray-900 dark:text-white font-semibold text-sm mb-3">
          {{ t('footer.newsletter.text') }}
        </h3>
        <form @submit.prevent="subscribeNewsletter">
          <div class="flex gap-2">
            <input
              v-model="newsletterEmail"
              type="email"
              required
              :placeholder="t('footer.newsletter.placeholder')"
              class="flex-1 min-w-0 px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            >
            <button
              type="submit"
              class="flex-shrink-0 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              {{ t('footer.newsletter.button') }}
            </button>
          </div>
          <Transition name="fade">
            <p v-if="isSubscribed" class="text-green-600 dark:text-green-400 text-xs mt-2">
              {{ t('footer.newsletter.success') }}
            </p>
          </Transition>
        </form>
      </div>

      <!-- Divider -->
      <div class="border-t border-gray-200 dark:border-gray-800 pt-6 sm:pt-8">
        <!-- Payment Methods & Security -->
        <div class="flex flex-col items-center gap-6 mb-6 sm:flex-row sm:justify-between">
          <!-- Payment Methods -->
          <div class="text-center sm:text-left">
            <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 sm:mb-3">
              {{ t('footer.paymentMethods') }}
            </p>
            <div class="flex items-center justify-center sm:justify-start gap-2 sm:gap-3">
              <div
                v-for="(method, index) in paymentMethods"
                :key="index"
                class="w-10 h-7 sm:w-12 sm:h-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded flex items-center justify-center"
                :title="method.name"
              >
                <UIcon :name="method.icon" class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
              </div>
            </div>
          </div>

          <!-- Security Badges -->
          <div class="text-center sm:text-right">
            <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 sm:mb-3">
              {{ t('footer.secureCheckout') }}
            </p>
            <div class="flex items-center justify-center sm:justify-end gap-2 sm:gap-3 flex-wrap">
              <div class="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <UIcon name="i-lucide-shield-check" class="w-3 h-3 sm:w-4 sm:h-4 text-green-600 dark:text-green-400" />
                <span class="text-xs font-semibold text-green-700 dark:text-green-400">SSL</span>
              </div>
              <div class="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <UIcon name="i-lucide-verified" class="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 dark:text-blue-400" />
                <span class="text-xs font-semibold text-blue-600 dark:text-blue-400">{{ t('footer.verified') }}</span>
              </div>
            </div>
          </div>
        </div>

        <dl
          v-if="businessDisclosureItems.length"
          class="mb-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-gray-500 dark:text-gray-400"
        >
          <div v-for="item in businessDisclosureItems" :key="item.label" class="flex gap-1">
            <dt class="font-semibold">{{ item.label }}:</dt>
            <dd>{{ item.value }}</dd>
          </div>
        </dl>

        <!-- Bottom Bar -->
        <div class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center">
          <span>&copy; {{ currentYear }} MiDiGi.NET &middot; {{ t('footer.rights') }}</span>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
